/**
 * Редактор подписи связи (edge label): открытие/закрытие инлайн-редактора, обновление waypoints в модели.
 */
import * as viewElements from './viewElements';

export function openConnectionLabelEditor(modeler, connectionViewId) {
	const graph = modeler.getGraph();
	if (!graph || !connectionViewId) return;
	const edge = graph.getCellById?.(connectionViewId);
	if (edge && typeof edge.isEdge === 'function' && edge.isEdge()) {
		openConnectionLabelEditorForCell(modeler, edge);
	}
}

export function openConnectionLabelEditorForCell(modeler, cell) {
	const graph = modeler.getGraph();
	if (!graph || !cell || typeof cell.isEdge !== 'function' || !cell.isEdge()) return;
	const VIEW_INDEX = modeler._currentViewIndex ?? 0;

	let labels = cell.getLabels?.() ?? [];
	if (labels.length === 0) {
		cell.appendLabel?.('');
		labels = cell.getLabels?.() ?? [];
	}
	const first = labels[0];
	const currentText = (first?.attrs?.text?.text ?? first?.attrs?.label?.text ?? '') || '';
	const edgeView = graph.findViewByCell?.(cell);
	let clientRect = {x: 0, y: 0, width: 120, height: 24};
	if (edgeView?.container) {
		const edgeBBox = cell.getBBox?.();
		if (edgeBBox) {
			const center = {
				x: edgeBBox.x + edgeBBox.width / 2,
				y: edgeBBox.y + edgeBBox.height / 2,
			};
			clientRect =
				graph.localToClient?.({
					x: center.x - 60,
					y: center.y - 12,
					width: 120,
					height: 24,
				}) ?? clientRect;
		}
	}
	openLabelEditor(modeler, {
		cell,
		graph,
		clientRect: {
			x: clientRect.x,
			y: clientRect.y,
			width: Math.max(clientRect.width, 80),
			height: Math.max(clientRect.height, 20),
		},
		initialValue: typeof currentText === 'string' ? currentText : '',
		onCommit: newText => {
			const id = cell.id;
			if ((cell.getLabels?.() ?? []).length > 0) {
				cell.removeLabelAt?.(0);
			}
			cell.appendLabel({
				attrs: {
					text: {
						text: newText,
					},
				},
			});
			const viewElement = viewElements.findViewElementById(modeler, id);
			if (viewElement) {
				viewElement.label = newText;
				// Сохраняем в relationshipRef.name, чтобы в Drawer «Имя» и при загрузке было то же значение
				const rel = viewElement.relationshipRef;
				if (rel) {
					if (rel.set) rel.set('name', newText);
					else rel.name = newText;
				}
			}
			modeler
				.getGraph()
				?.emit?.('connection:label:updated', {connectionViewId: id, label: newText});
		},
	});
}

export function openLabelEditor(modeler, opts) {
	const {cell, clientRect, initialValue, onCommit} = opts;
	closeLabelEditor(modeler);

	const wrap = document.createElement('div');
	wrap.className = 'x6-label-editor-wrap';
	Object.assign(wrap.style, {
		position: 'fixed',
		left: `${clientRect.x}px`,
		top: `${clientRect.y}px`,
		width: `${clientRect.width}px`,
		height: `${clientRect.height}px`,
		zIndex: '10000',
		boxSizing: 'border-box',
		padding: '2px 4px',
		background: '#fff',
		border: '1px solid #1890ff',
		borderRadius: '2px',
		boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
		overflow: 'hidden',
	});

	const textarea = document.createElement('textarea');
	textarea.value = initialValue;
	Object.assign(textarea.style, {
		width: '100%',
		height: '100%',
		padding: 0,
		margin: 0,
		border: 'none',
		outline: 'none',
		resize: 'none',
		fontSize: '12px',
		fontFamily: 'inherit',
		lineHeight: '1.4',
		wordWrap: 'break-word',
		overflow: 'auto',
		boxSizing: 'border-box',
	});
	wrap.appendChild(textarea);

	const commit = () => {
		const value = (textarea.value ?? '').trim();
		closeLabelEditor(modeler);
		onCommit(value);
	};
	const cancel = () => {
		closeLabelEditor(modeler);
	};

	textarea.addEventListener('blur', () => commit());
	textarea.addEventListener('keydown', e => {
		if (e.key === 'Escape') {
			e.preventDefault();
			cancel();
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			commit();
		}
	});

	document.body.appendChild(wrap);
	textarea.focus();
	textarea.select();

	modeler._labelEditorWrap = wrap;
}

export function closeLabelEditor(modeler) {
	const wrap = modeler._labelEditorWrap;
	if (wrap) {
		modeler._labelEditorWrap = null;
		const parent = wrap.parentNode;
		if (parent && parent.contains(wrap)) {
			try {
				parent.removeChild(wrap);
			} catch (_) {
				// Node may have been removed by blur/commit; ignore.
			}
		}
	}
}

export function updateConnectionWaypointsInModel(modeler, connectionViewId, bendpoints) {
	const viewElement = viewElements.findViewElementById(modeler, connectionViewId);
	if (!viewElement || !modeler._moddle) return;
	const waypointEls = (bendpoints || []).map(p =>
		modeler._moddle.create('archimate:Waypoint', {x: p.x, y: p.y})
	);
	const wp = modeler._moddle.create('archimate:Waypoints', {waypoints: waypointEls});
	if (wp.waypoints) {
		for (const w of wp.waypoints) {
			if (w) w.$parent = wp;
		}
	}
	wp.$parent = viewElement;
	viewElement.waypointsNode = wp;
}
